import {
  Avatar,
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Popover,
  PopoverTrigger,
  Spacer,
  Text,
  Tooltip,
  VStack
} from '@chakra-ui/react';
import { AmplifyCore } from '@codametrix/ui-common';
import { BiomeIcon, IconName, Logomark4C } from '@codametrix/verdigris';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setBackToWorklistPath } from '../../actions/ui';
import { fetchVerticalNavFlag } from '../../actions/vertical-nav.action';
import { useNavInstrumentation } from '../../core/nav-intrumentation.context';
import { filterNavForVerticalNav } from '../../core/navigation';
import { routedToGradingScreen } from '../../core/utils/isCQAGradingScreen';
import { fetchVerticalViewSelector } from '../../core/vertical-view-selector';
import { selectHidePrioritizeNav } from '../../reducers/vertical-nav.reducer';
import theme from '../../theme';
import DashboardSelection from '../dashboard-selection/dashboard-selection-component';
import { OrganizationServiceline } from '../org-serviceline/org-serviceline';
import { UserProfile } from '../user-profile/user-profile';
import { navComponents } from './nav-icons';
import './primary-global-navigation.scss';
import { styles } from './primary-global-navigation.styles';
import { SubNav } from './sub-nav';

/**
 * CDMX-19305 — the "Worklist" nav item shows as "Amplify" except where the
 * `vertical-view-selector` configuration resolves to true, which is the HFHS view; there
 * it keeps "Worklist".
 *
 * The wildcard configuration row is false, so the rename is the default and a tenant
 * keeps "Worklist" only where something scoped to it, or to its health system, opts it
 * out. See core/vertical-view-selector.
 *
 * Label only — `id`, `path` and `observationService` are untouched, so routing,
 * permissions and instrumentation are unaffected.
 */
const WORKLIST_NAV_ID = 'worklist';
const WORKLIST_NAV_LABEL = 'Worklist';
const AMPLIFY_NAV_LABEL = 'Amplify';

function pathnameIsAmplifyBench(pathname: string): boolean {
  return (
    /^\/cmx\/worklist\/[^/]+\/case\/[^/]+\/case-run\//.test(pathname) ||
    /^\/cmx\/coding-quality\/[^/]+\/case\/[^/]+\/case-run\//.test(pathname) ||
    routedToGradingScreen(pathname)
  );
}

export interface PrimaryGlobalNavigationProps {
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  disabled?: boolean;
  name?: string;
  profile_src?: string;
  tabColor?: string;
  context: CMx.Context;
  actionGroups: CMxAPI.NavActionGroup[];
  handleOrganizationChoice: (orgChoice: CMx.OrganizationFormChoice) => void;
  handleServiceLineChoice: (
    serviceLineChoice: CMx.ServiceLine,
    context: CMx.Context,
    actionGroups: CMxAPI.NavActionGroup[]
  ) => void;
  onSelection: (selction: CMxAPI.NavAction) => void;
  onLogout: () => void;
  activeOrg: string;
  activeUser: CMxAPI.User;
  serviceLine?: CMx.ServiceLine;
  openAccountSettings: () => void;
  openCapturedTime: () => void;
  listStatus: (activeTenantId: string) => void;
  updateStatus: (
    status: string,
    user: CMxAPI.User,
    context: CMx.Context
  ) => void;
  showDashboardOptions: boolean;
  getSubNavPaths: (userDetails: {
    tenantId: string;
    serviceLine: string;
  }) => void;
}

export const PrimaryGlobalNavigation: React.FC<PrimaryGlobalNavigationProps> = ({
  actionGroups: actionGroupsProp,
  activeOrg: activeOrgDisplay,
  activeUser,
  context: cmxContext,
  context,
  handleOrganizationChoice,
  handleServiceLineChoice,
  listStatus,
  name,
  onLogout,
  onSelection,
  openAccountSettings,
  openCapturedTime,
  profile_src,
  serviceLine,
  showDashboardOptions,
  tabColor,
  updateStatus,
  className = '',
  disabled = false,
  getSubNavPaths = (userDetails: { tenantId: string; serviceLine: string }) =>
    {},
  ...props
}) => {
  const classes = styles(tabColor);
  const dispatch = useDispatch();
  const location = useLocation();

  // Until the flag resolves the nav renders unchanged.
  const navTenantId = useSelector(
    (state: any) => state.ui?.context?.activeContext?.tenantId as string | undefined
  );
  const navServiceLine = useSelector(
    (state: any) => state.ui?.serviceLine?.name as string | undefined
  );
  // Hiding requires the remote's consent as well — see selectHidePrioritizeNav.
  const hidePrioritize = useSelector((state: any) =>
    selectHidePrioritizeNav(state, navTenantId, navServiceLine)
  );

  useEffect(() => {
    if (navTenantId && navServiceLine) {
      dispatch(
        fetchVerticalNavFlag({
          tenantId: navTenantId,
          serviceLine: navServiceLine
        }) as any
      );
    }
  }, [dispatch, navTenantId, navServiceLine]);

  const actionGroups = useMemo(
    () => filterNavForVerticalNav(actionGroupsProp, hidePrioritize),
    [actionGroupsProp, hidePrioritize]
  );

  const getActiveServiceLine = useCallback(() => {
    const serviceLineName = (serviceLine?.displayName || serviceLine?.name) ?? '';
    return serviceLineName;
  }, [serviceLine]);

  const [activeServiceLineName, setActiveServiceLineName] = useState<string>(
    getActiveServiceLine()
  );

  //region Parse out which analytics sections to display
  const subNavPaths = useSelector(
    (state: CMx.ApplicationState) => state.analytics.dashboardPaths
  );

  const tableauDashboardResourceId = context.activeContext?.resources.find(
    resource => resource.resourceName === 'tableau_dashboard'
  )?.resourceId;

  // Get outer sections (CMX Insights, Pre-Live Analytics) //
  const analyticsSections = context.activeContext?.resources.filter(
    resource =>
      resource.parentId === tableauDashboardResourceId &&
      AmplifyCore.benchEditable.verifyServiceLine(
        resource,
        context.activeContext?.serviceLineName ?? ''
      )
  );

  let analyticsSubnavs: {
    [key: string]: {
      name: string;
      dashboards: { dashboardName: string; dashboardResource: string }[];
    };
  } = {};

  if (analyticsSections) {
    // Get inner sections (Operational Analytics, Case Analytics,  etc...) //
    analyticsSections.forEach(section => {
      const subnavResources = context.activeContext?.resources.filter(
        resource =>
          resource.parentId === section.resourceId &&
          Object.keys(subNavPaths).includes(resource.resourceName)
      );

      if (!subnavResources) return;
      let sectionLabel = '';
      try {
        sectionLabel = JSON.parse(section.metadata).label ?? '';
      } catch {
        // Metadata is not valid JSON — skip the label rather than crashing nav
      }
      analyticsSubnavs[section.resourceName] = {
        name: sectionLabel,
        dashboards: subnavResources.map(resource => ({
          dashboardName: resource.label,
          dashboardResource: resource.resourceName
        }))
      };
    });
  }
  //endregion

  useEffect(() => {
    if (context.activeContext) {
      getSubNavPaths({
        tenantId: context.activeContext?.tenantId ?? '',
        serviceLine: context.activeContext?.serviceLineName ?? ''
      });
    }
  }, [context.activeContext, getSubNavPaths]);

  useEffect(() => {
    setActiveServiceLineName(getActiveServiceLine());
  }, [getActiveServiceLine, context]);

  // CDMX-19305 — resolve the worklist nav label from `vertical-view-selector`.
  //
  // "Worklist" is the fail-safe default, held for both a not-yet-loaded context and an
  // `unavailable` read. Only an explicit answer renames the item, so a failed read can
  // never rebrand the nav of a tenant that should keep "Worklist"; the cost is that a
  // renamed tenant briefly sees the old label, which corrects itself on the next mount.
  // That asymmetry is why the reader distinguishes resolved from unavailable.
  const [worklistNavLabel, setWorklistNavLabel] = useState<string>(
    WORKLIST_NAV_LABEL
  );
  const configTenantId = context.activeContext?.tenantId;
  const configServiceLine = context.activeContext?.serviceLineName;

  useEffect(() => {
    if (!configTenantId || !configServiceLine) {
      return;
    }

    // Guard against a context switch resolving out of order and leaving the previous
    // tenant's label on screen.
    let cancelled = false;

    fetchVerticalViewSelector(configTenantId, configServiceLine).then(result => {
      if (cancelled || result.status !== 'resolved') {
        return;
      }
      setWorklistNavLabel(
        result.enabled ? AMPLIFY_NAV_LABEL : WORKLIST_NAV_LABEL
      );
    });

    return () => {
      cancelled = true;
    };
  }, [configTenantId, configServiceLine]);

  const displayLabelFor = useCallback(
    (action: CMxAPI.NavAction): string =>
      action?.id === WORKLIST_NAV_ID ? worklistNavLabel : action?.label ?? '',
    [worklistNavLabel]
  );

  useEffect(() => {
    if (pathnameIsAmplifyBench(location.pathname)) {
      return;
    }
    actionGroups.forEach(actionGroup => {
      actionGroup.options.forEach(action => {
        if (action.active) {
          dispatch(setBackToWorklistPath(action));
        }
      });
    });
  }, [actionGroups, dispatch, location.pathname]);

   const navInstrumentation = useNavInstrumentation();
  const selectionWrapper = (actionGroupOption: CMxAPI.NavAction) => {
    const serviceContext = actionGroupOption.observationService || actionGroupOption.id;
    navInstrumentation.setGlobalContext(serviceContext);
    navInstrumentation.trackItemActivated(actionGroupOption, 'PRIMARY_NAV_CLICK');
    dispatch(setBackToWorklistPath(actionGroupOption));
    onSelection(actionGroupOption);
  };

  const [switcherOpened, setSwitcherOpened] = useState<boolean>(false);
  const [userProfileOpened, setUserProfileOpened] = useState<boolean>(false);
  const [dashboardSelectionOpened, setDashboardSelectionOpened] = useState<
    boolean
  >(false);

  const userInitials =
    activeUser?.firstName && activeUser?.lastName
      ? activeUser.firstName?.charAt(0) + ' ' + activeUser.lastName?.charAt(0)
      : '🧑‍⚕️';

  function handleSwitcherClick(): void {
    setSwitcherOpened(!switcherOpened);
  }

  function handleUserProfileClick(): void {
    setUserProfileOpened(!userProfileOpened);
  }

  const navItemComponentMap: Record<string, React.ComponentType<any>> = {
    Insights: DashboardSelection
  };

  const getComponentForNavItem = (navItemLabel: string) => {
    return navItemComponentMap[navItemLabel]; //|| DefaultComponent;
  };

  /* Currently only the Analytics section has subnavs. */
  const hasAnalyticsSubnavs =
    (analyticsSections && analyticsSections?.length > 1) ||
    (analyticsSubnavs &&
      Object.keys(analyticsSubnavs).length > 0 &&
      analyticsSubnavs[Object.keys(analyticsSubnavs)[0]]?.dashboards?.length >
        1);

  return (
    <Grid sx={classes['.mainVStack']} gridTemplateRows="min-content">
      <Box
        data-testid="primary-global-navigation-button"
        sx={classes['.mainGrid']}>
        <Popover
          closeOnBlur={true}
          placement="right"
          isOpen={switcherOpened}
          data-testid="global-navigation-switcher"
          onClose={() => {
            setSwitcherOpened(false);
          }}>
          {({ isOpen, onClose }) => (
            <>
              {/* @ts-ignore */}
              <PopoverTrigger>
                <Flex
                  sx={classes['.orgBox']}
                  data-testid="global-navigation-switcher-trigger"
                  className={`hoverCursor ${
                    switcherOpened ? 'popOverSelected' : ''
                  }`}
                  onClick={() => handleSwitcherClick()}>
                  <Box>
                    <Tooltip label={activeOrgDisplay} placement="left">
                      <Text sx={classes['.orgServiceText']}>
                        {activeOrgDisplay}
                      </Text>
                    </Tooltip>
                    <Tooltip
                      label={serviceLine?.displayName || serviceLine?.name}
                      placement="left">
                      <Text sx={classes['.orgServiceText']}>
                        {activeServiceLineName}
                      </Text>
                    </Tooltip>
                  </Box>
                  <Spacer />
                  <BiomeIcon
                    iconName={IconName.Chevron_Right}
                    iconSize="12"
                    titleText="More"
                  />
                </Flex>
              </PopoverTrigger>
              <OrganizationServiceline
                context={context}
                actionGroups={actionGroups}
                handleOrganizationChoice={handleOrganizationChoice}
                handleServiceLineChoice={handleServiceLineChoice}
                onSelection={onSelection}
                activeOrg={activeOrgDisplay}
                activeUser={activeUser}
                serviceLine={serviceLine}
                openAccountSettings={openAccountSettings}
                setSwitcherOpened={setSwitcherOpened}
              />
            </>
          )}
        </Popover>
      </Box>
      <VStack
        textStyle="Subtitle 2"
        sx={classes['.secondVStack']}
        spacing={0}>
        <VStack sx={classes['.avatarVstack']}>
          <Popover
            closeOnBlur={true}
            placement="right"
            isOpen={userProfileOpened}
            onClose={() => {
              setUserProfileOpened(false);
            }}>
            <PopoverTrigger>
              <Flex sx={classes['.avatarBox']}>
                <Avatar
                  name={userInitials}
                  src={profile_src}
                  sx={
                    userProfileOpened
                      ? classes['.selectedUserNameAvatar']
                      : classes['.usernNameAvatar']
                  }
                  onClick={() => {
                    handleUserProfileClick();
                  }}
                  data-testid={'userAvatar'}
                  className={`hoverCursor`}
                />
              </Flex>
            </PopoverTrigger>
            <UserProfile
              activeUser={activeUser}
              onLogout={onLogout}
              openAccountSettings={openAccountSettings}
              openCapturedTime={openCapturedTime}
              userInitials={userInitials}
              profile_src={profile_src}
              listStatus={listStatus}
              updateStatus={updateStatus}
            />
          </Popover>
        </VStack>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          alignItems="stretch">
          {actionGroups
            ?.filter(actionGroup =>
              actionGroup.options.some(action => action.enabled)
            )
            .flatMap(actionGroup =>
              (actionGroup.options ?? []).filter(option => option.enabled)
            )
            .map((actionGroupOption, navIndex) => {
              const usesSubNav =
                hasAnalyticsSubnavs &&
                (actionGroupOption.navSubResources?.length ?? 0) > 0;
              // The icon map, the React key and the test id are all keyed off the
              // displayed label, so they have to move with it or the renamed item loses
              // its icon.
              const displayLabel = displayLabelFor(actionGroupOption);
              return (
                <Box
                  key={displayLabel}
                  width="100%"
                  position="relative"
                  pt={
                    navIndex === 0
                      ? usesSubNav
                        ? theme.space[8]
                        : theme.space[16]
                      : 0
                  }
                  pb={theme.space[20]}
                  onClick={() => {
                    selectionWrapper(actionGroupOption);
                  }}
                  className={`hoverCursor ${
                    actionGroupOption?.active ? 'navItem' : ''
                  }`}
                  data-testid={`${displayLabel}tab`}>
                  <VStack
                    spacing={theme.space[2]}
                    sx={classes['.navItemVStack']}>
                    {usesSubNav ? (
                      <SubNav
                        tabColor={tabColor}
                        selectionOpened={dashboardSelectionOpened}
                        setSelectionOpened={setDashboardSelectionOpened}
                        actionGroupOptionLabel={displayLabel}
                        childComponent={getComponentForNavItem(displayLabel)}
                      />
                    ) : (
                      <>
                        <Grid sx={classes['.iconWrapper']}>
                          {actionGroupOption?.icon
                            ? navComponents[displayLabel]
                            : actionGroupOption?.icon}
                        </Grid>
                        <Text className="action-group-spacing" align={'center'}>
                          {displayLabel}
                        </Text>
                      </>
                    )}
                  </VStack>
                  <Divider
                    orientation="vertical"
                    sx={classes['.dividerVertical']}
                  />
                </Box>
              );
            })}
        </Box>
      </VStack>
      <GridItem sx={classes['.navLogo']}>
        <Logomark4C titleText="Codametrix" />
      </GridItem>
    </Grid>
  );
};

PrimaryGlobalNavigation.displayName = 'PrimaryGlobalNavigation';
